import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LocalDate } from '../../../../core/pojo/date-variable';
import { MetricDataRole } from '../../dto/MetricData';
import { MetricGroup } from '../../dto/MetricGroup';
import { MetricState } from '../../dto/MetricState';
import { CompanyWithMetricsDTO } from "../../../company/dto/CompanyWithMetricsDTO";
import { MetricGroupFormWrapper } from "../../dto/MetricGroupForm";

@Injectable({
  providedIn: 'root',
})
export class MetricComponentStateService {
  private selectedCompanySource = new BehaviorSubject<CompanyWithMetricsDTO>(undefined);
  private metricStateSource = new BehaviorSubject<MetricState>(MetricState.noActionSelected);
  private periodsToDeleteSource = new BehaviorSubject<LocalDate[]>([]);
  private selectedMetricRoleSource = new BehaviorSubject<MetricDataRole>(MetricDataRole.ACTUAL);
  private fetchMetricGroupsSource = new BehaviorSubject<void>(null);
  private metricGroupsToSaveSource = new BehaviorSubject<MetricGroupFormWrapper>(null);
  private customMetricGroupToCRUDSource = new BehaviorSubject<MetricGroup>(null);

  // all vars are immutable.
  getSelectedCompany = this.selectedCompanySource.asObservable();
  getMetricState = this.metricStateSource.asObservable();
  getPeriodsToDelete = this.periodsToDeleteSource.asObservable();
  getSelectedMetricRole = this.selectedMetricRoleSource.asObservable();
  getFetchMetricGroups = this.fetchMetricGroupsSource.asObservable();
  getMetricGroupsToSave = this.metricGroupsToSaveSource.asObservable();
  getCustomMetricGroupToCRUD = this.customMetricGroupToCRUDSource.asObservable();

  setSelectedCompany(company: CompanyWithMetricsDTO): void {
    this.selectedCompanySource.next(company);
  }

  setMetricState(state: MetricState): void {
    this.metricStateSource.next(state);
  }

  setPeriodsToDelete(periods: LocalDate[]): void {
    this.periodsToDeleteSource.next(periods);
  }

  setSelectedMetricRole(role: MetricDataRole): void {
    this.selectedMetricRoleSource.next(role);
  }

  /**
   * This function first calls set null to the groups to be saved.
   * This is done because we want to make sure user does not click save button with the old data,
   * which has to be updated after the component receive fetchMetricGroupsSource.
   * Example: after saving custom metric group, the edit state is set.
   * We want to disable "save" button till we receive the new fetched metric groups.
   */
  setFetchMetricGroups(): void {
    this.setMetricGroupsToSave(null);
    this.fetchMetricGroupsSource.next();
  }

  setMetricGroupsToSave(metricGroups: MetricGroupFormWrapper): void {
    this.metricGroupsToSaveSource.next(metricGroups);
  }

  setCustomMetricGroupToCRUD(metricGroup: MetricGroup): void {
    this.customMetricGroupToCRUDSource.next(metricGroup);
  }

  finishGroupManage(): void {
    this.setMetricState(MetricState.noActionSelected);
    this.setMetricGroupsToSave(null);
    this.setFetchMetricGroups();
  }

  finishPeriodBulkDelete(isSuccessful: boolean): void {
    this.setMetricState(MetricState.noActionSelected);
    this.setPeriodsToDelete([]);
    if (isSuccessful) {
      this.setFetchMetricGroups();
    }
  }

  finishCRUDMetricGroup(isSuccessful: boolean): void {
    this.setMetricState(MetricState.editAll);
    if (isSuccessful) {
      this.setFetchMetricGroups();
    }
  }

  finishAnyActivity(): void {
    this.setMetricState(MetricState.noActionSelected);
    this.setFetchMetricGroups();
  }

  showCreateCustomGroupModal(): void {
    this.setCustomMetricGroupToCRUD(new MetricGroup());
    this.setMetricState(MetricState.modalCustomGroup);
  }
}
